(() => {
  const parseVideo = (media) => {
    const provider = (media.dataset.provider || '').trim();
    const id = (media.dataset.videoId || '').trim();
    if (provider && id) return { provider, id };

    const url = (media.dataset.videoUrl || '').trim();
    if (!url) return null;

    const youtube = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/
    );
    if (youtube) return { provider: 'youtube', id: youtube[1] };

    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) return { provider: 'vimeo', id: vimeo[1] };

    return null;
  };

  const buildSrc = ({ provider, id }, autoplay) => {
    if (provider === 'vimeo') {
      const params = ['api=1', 'playsinline=1'];
      if (autoplay) params.push('autoplay=1', 'muted=1');
      return `https://player.vimeo.com/video/${id}?${params.join('&')}`;
    }

    const params = ['enablejsapi=1', 'playsinline=1', 'rel=0', 'modestbranding=1'];
    if (autoplay) params.push('autoplay=1', 'mute=1');
    return `https://www.youtube.com/embed/${id}?${params.join('&')}`;
  };

  const command = (iframe, video, action) => {
    if (!iframe || !iframe.contentWindow || !video) return;
    if (video.provider === 'vimeo') {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: action === 'play' ? 'play' : 'pause' }),
        '*'
      );
      return;
    }
    iframe.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: action === 'play' ? 'playVideo' : 'pauseVideo',
        args: [],
      }),
      '*'
    );
  };

  const createPlayer = (panel) => {
    const media = panel.querySelector('[data-le-keynotes-media]');
    const frame = media && media.querySelector('[data-le-keynotes-frame]');
    const video = media ? parseVideo(media) : null;
    const autoplay = media && media.dataset.autoplay === 'true';

    let iframe = null;

    const mount = (shouldAutoplay) => {
      if (!frame || !video || iframe) return;
      iframe = document.createElement('iframe');
      iframe.src = buildSrc(video, shouldAutoplay);
      iframe.title = 'Keynote video';
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      iframe.setAttribute('allowfullscreen', 'allowfullscreen');
      iframe.loading = shouldAutoplay ? 'eager' : 'lazy';
      frame.appendChild(iframe);
      media.classList.add('is-ready');
    };

    const pause = () => {
      command(iframe, video, 'pause');
    };

    const activate = () => {
      if (!iframe) {
        mount(autoplay);
        return;
      }
      if (autoplay) command(iframe, video, 'play');
    };

    return { mount, pause, activate };
  };

  const initKeynotes = (root) => {
    if (root.dataset.leKeynotesReady === 'true') return;
    root.dataset.leKeynotesReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-le-keynotes-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-le-keynotes-panel]'));
    if (panels.length === 0) return;

    const players = panels.map(createPlayer);

    let activeIndex = panels.findIndex((panel) => panel.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;

    players[activeIndex]?.activate();

    const setActive = (index) => {
      if (index === activeIndex) return;
      players[activeIndex]?.pause();
      activeIndex = index;

      tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      players[activeIndex]?.activate();

      document.dispatchEvent(
        new CustomEvent('le:keynote-change', {
          detail: {
            index: activeIndex,
            sectionId: root.id || '',
          },
        })
      );
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const index = Number.parseInt(tab.dataset.index || '0', 10);
        if (!Number.isFinite(index)) return;
        setActive(index);
      });
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-le-keynotes]').forEach(initKeynotes);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
